import { useCallback, useMemo, useState } from "react";
import type {
  AirBookingFlightItinerarySlice,
  ItinerarySeats,
  Person,
  Seat,
} from "@b2bportal/air-booking-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ButtonWrap, Divider, MobilePopoverCard } from "@hopper-b2b/ui";
import Chevron from "../../../assets/client/chevron.svg?react";
import Close from "../../../assets/client/close.svg?react";
import ExternalLink from "../../../assets/client/external-link.svg?react";
import SeatIcon from "../../../assets/client/seat.svg?react";
import { openExternalUrl } from "../../../utils/nubankHandoff";
import styles from "./styles.module.scss";

interface TripsFlightsDetailsSeatsProps {
  itinerarySeats?: ItinerarySeats;
  passengers: Person[];
  airlineHomePage?: string;
  slices?: AirBookingFlightItinerarySlice[];
  lapInfantIds?: string[];
}

interface SeatSegment {
  origin: string;
  destination: string;
  outgoing: boolean;
  seats: Seat[];
}

export const TripsFlightsDetailsSeats = ({
  itinerarySeats,
  passengers,
  airlineHomePage,
  slices,
  lapInfantIds,
}: TripsFlightsDetailsSeatsProps) => {
  const [showPopover, setShowPopover] = useState(false);
  const { t, formatFiatCurrency } = useI18nContext();

  const seats = itinerarySeats?.seats;
  const hasSeats = !!seats && seats.length > 0;

  const displayPassengers = useMemo(
    () => passengers.filter((p) => !lapInfantIds?.includes(p.id)),
    [passengers, lapInfantIds]
  );

  const segments = useMemo(() => {
    const result: SeatSegment[] = (slices ?? []).flatMap((slice, sliceIndex) =>
      slice.segments.map((seg) => ({
        origin: seg.origin.locationCode,
        destination: seg.destination.locationCode,
        outgoing: sliceIndex === 0,
        seats: [] as Seat[],
      }))
    );

    for (const seat of seats ?? []) {
      const segment = result.find(
        (s) => s.origin === seat.origin && s.destination === seat.destination
      );
      if (segment) segment.seats.push(seat);
    }

    return result;
  }, [slices, seats]);

  const getPassengerSeat = useCallback(
    (personId: string, segment: SeatSegment): Seat | undefined => {
      return segment.seats.find((s) => s.personId === personId);
    },
    []
  );

  const formatSeatPrice = useCallback(
    (seat: Seat): string => {
      return formatFiatCurrency({
        currencyCode: seat.currency,
        value: seat.price,
      });
    },
    [formatFiatCurrency]
  );

  const grandTotal = useMemo(() => {
    if (!hasSeats || !seats) return "";

    const totalValue = seats.reduce((sum, seat) => sum + seat.price, 0);
    if (totalValue === 0) return "";

    return formatFiatCurrency({
      currencyCode: seats[0].currency,
      value: totalValue,
    });
  }, [seats, hasSeats, formatFiatCurrency]);

  const togglePopover = useCallback(() => {
    setShowPopover((prev) => !prev);
  }, []);

  return (
    <>
      <Divider className="mobile-trip-details-divider" />
      <ButtonWrap
        onClick={togglePopover}
        className={styles.TripsFlightsDetailsSeats}
      >
        <div className="left">
          <SeatIcon className="seats-icon" />
          <div className="content">
            <p className="title">{t("seats.title")}</p>
          </div>
        </div>
        <Chevron className="arrow-icon" />
      </ButtonWrap>

      <MobilePopoverCard
        open={showPopover}
        className={styles.SeatsInfoPopover}
        onClose={togglePopover}
        centered={false}
        fullScreen
        contentClassName="seats-info-popover-content"
        bottomButton={
          airlineHomePage ? (
            <button
              className="cta-button"
              onClick={() => openExternalUrl(airlineHomePage)}
            >
              {t("tripsDetails.accessAirlineWebsite")}
              <ExternalLink className="cta-icon" />
            </button>
          ) : undefined
        }
      >
        <>
          <ButtonWrap onClick={togglePopover}>
            <Close className="close" />
          </ButtonWrap>

          <h2 className="seats-details-title">{t("seats.title")}</h2>

          <div className="callout-box">
            <p className="callout-title">{t("seats.calloutTitle")}</p>
            <p className="callout-description">
              {t("seats.calloutDescription")}
            </p>
          </div>

          <div className="seats-segments">
            {segments.map((segment) => (
              <div
                className="segment-section"
                key={`${segment.origin}-${segment.destination}`}
              >
                <p className="segment-label">
                  {segment.outgoing ? t("outbound") : t("seats.return")}
                  {`: ${segment.origin} - ${segment.destination}`}
                </p>

                {displayPassengers.map((passenger) => {
                  const seat = getPassengerSeat(passenger.id, segment);
                  return (
                    <div
                      className="passenger-row"
                      key={`seat-${segment.origin}-${passenger.id}`}
                    >
                      <div className="passenger-info">
                        <p className="passenger-name">
                          {passenger.givenName} {passenger.surname}
                        </p>
                        <p className={seat ? "seat-number" : "seat-random"}>
                          {seat ? seat.seatId.value : t("seats.randomSeat")}
                        </p>
                      </div>
                      {seat && seat.price > 0 && (
                        <p className="seat-cost">{formatSeatPrice(seat)}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {grandTotal && (
            <div className="total-row">
              <p className="total-label">{t("total")}</p>
              <p className="total-value">{grandTotal}</p>
            </div>
          )}
        </>
      </MobilePopoverCard>
    </>
  );
};
